import { Card, CardBody } from "@chakra-ui/react";
import AppPage from "layouts/AppPage";
import { useParams } from "react-router";
import { emailKeys, useEmailDetailQuery } from "features/Resources/Email/hooks";
import QueryError from "components/Error/QueryError";
import { usePlatformEventCable, CollapsibleHistory } from "features/PlatformEvents";
import EmailForm from "features/Resources/Email/Layout/EmailForm";
import SentEmail from "features/Resources/Email/Layout/SentEmail";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { INTERNAL_TEAM_ROUTES } from "definitions/constants/routeConstants";
import CaminoBreadcrumbs from "components/Breadcrumbs";
import { newHireJourneyPathById } from "helpers/url";
import { truncateEnd } from "helpers/string";

export default function EmailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error: queryError, isError } = useEmailDetailQuery(id);
  const { data: newHireJourney, isFetched } = useNewHireJourneyDetailQuery(data?.newHireJourneyId);

  usePlatformEventCable({
    entityType: "Email",
    entityId: data?.id,
    enabled: !!data?.id,
    detailQueryKeys: data?.id ? emailKeys.detail(data.id) : undefined,
  });

  if (!id) {
    return <AppPage>Email not found</AppPage>;
  }

  if (isLoading) {
    return <AppPage>Loading...</AppPage>;
  }

  if (!data && isError) {
    return <QueryError queryError={queryError} resourceName="Email" id={id} />;
  }

  return (
    <AppPage>
      <CaminoBreadcrumbs
        breadcrumbInfos={[
          { name: "New Hire Journeys", toUrl: INTERNAL_TEAM_ROUTES.newHireJourneys.index },
          {
            name: isFetched && newHireJourney?.newHire.fullName ? `${newHireJourney.newHire.fullName}` : "Onboarding",
            toUrl: newHireJourneyPathById(newHireJourney?.id),
          },
          { name: `Email '${truncateEnd(data?.name)}'` },
        ]}
      />
      {data && (
        <Card>
          <CardBody>
            {["skipped", "removed", "delivered"].includes(data.status) ? (
              <SentEmail email={data} />
            ) : (
              <EmailForm
                data={data}
                includeHeader
                includeMenu
                includeTemplate
                includeStatus
                isPending={newHireJourney?.isPending ?? false}
              />
            )}
            <CollapsibleHistory entityId={data.id} entityType="Email" eventType="email" />
          </CardBody>
        </Card>
      )}
    </AppPage>
  );
}
